import { decryptData, encryptData } from "./encryption";
import axios from "axios";

axios.defaults.baseURL = process.env.REACT_APP_API_URL;

// Utility to get token & loginType dynamically before each request
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Utility to check if data is encrypted
const isEncrypted = (data) => typeof data === "string" && data.includes(":");

// Add `loginType` to every request automatically
axios.interceptors.request.use((config) => {
  const loginType = localStorage.getItem("loginType") || "employer";
  if (loginType) {
    config.params = { ...config.params, loginType };
  }
  config.headers = { ...config.headers, ...getAuthHeaders() };
  return config;
});

// Add response interceptor to handle token expiration globally
axios.interceptors.response.use(
  (response) => response, // Return successful responses as-is
  (error) => {
    // Check for token expiration
    if (error.response?.status === 401) {
      let errorData = error.response.data;
      let errorMessage = "";
      
      try {
        // Try to decrypt error message if encrypted
        if (typeof errorData === 'string' && isEncrypted(errorData)) {
          errorData = decryptData(errorData);
        }
        
        // Extract error message
        if (typeof errorData === 'object' && errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          errorMessage = "Authentication error";
        }
      } catch (decryptionError) {
        console.error("Failed to decrypt error response:", decryptionError);
        errorMessage = "Authentication error";
      }
      
      // Check if it's a token expiration error
      if (errorMessage.includes("jwt expired") || 
          errorMessage.includes("Token expired") || 
          errorMessage.includes("expired")) {
        
        console.log("Token expired, cleaning up and redirecting...");
        
        // Clear expired token and user data
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("enterpriseId");
        localStorage.removeItem("loginType");
        
        // Show user-friendly message
        alert("Your session has expired. Please log in again.");
        
        // Redirect to login page
        setTimeout(() => {
          window.location.href = "/";
        }, 100);
      }
    }
    
    return Promise.reject(error);
  }
);

const request = async (method, endpoint, data = null, responseType = "json", setErrorMessage) => {
  try {
    console.info(`🚀 Sending API request: ${method.toUpperCase()} ${endpoint}`);
    if (data) {
      console.info(`📋 Request data:`, data);
    }
    
    let response;

    // Encrypt request data if needed
    let requestBody = null;
    if (data) {
      const encryptedData = encryptData(data);
      
      // Different backend routes expect different formats based on their implementation:
      // Routes that expect { encryptedData: "..." }: enterprise, subscription
      // Routes that expect { data: "..." }: user, auth, permissions, applicants, employees, forms, messages, users
      
      const routesUsingEncryptedDataField = ['enterprise', 'subscription'];
      const usesEncryptedDataFormat = routesUsingEncryptedDataField.some(route => 
        endpoint.includes(`/${route}`) || endpoint === route || endpoint.startsWith(`${route}/`)
      );
      
      if (usesEncryptedDataFormat) {
        requestBody = { encryptedData: encryptedData };
      } else {
        requestBody = { data: encryptedData };
      }
    }

    // Send the request based on the HTTP method
    switch (method) {
      case "get":
        response = await axios.get(endpoint);
        break;
      case "post":
        response = await axios.post(endpoint, requestBody);
        break;
      case "put":
        response = await axios.put(endpoint, requestBody);
        break;
      case "delete":
        response = await axios.delete(endpoint);
        break;
      case "patch":
        response = await axios.patch(endpoint, requestBody);
        break;
      default:
        throw new Error(`Unsupported method: ${method}`);
    }

    // console.debug("✅ API response received:", response);

    // ✅ Automatically decrypt response if encrypted
    let responseData = response.data;
    if (responseType === "json" && isEncrypted(responseData)) {
      try {
        responseData = decryptData(responseData);
        // console.debug("🔓 Decrypted API response:", responseData);
      } catch (decryptionError) {
        console.error("❌ Failed to decrypt response:", decryptionError);
        throw new Error("Failed to decrypt server response.");
      }
    }

    return responseData; // Return decrypted or raw data

  } catch (error) {
    console.error("❌ API request error:", error);

    let errorMessage = "An error occurred. Please try again.";

    if (error.response?.data) {
      try {
        let responseData = error.response.data;

        // ✅ Check and decrypt error response if needed
        if (isEncrypted(responseData)) {
          responseData = decryptData(responseData);
          console.debug("🔓 Decrypted error response:", responseData);
        }

        // ✅ Extract message from decrypted error response
        errorMessage = responseData.message || errorMessage;
      } catch (decryptionError) {
        console.error("❌ Failed to decrypt error response:", decryptionError);
        errorMessage = "Failed to decrypt the error message.";
      }
    }

    // Handle specific JWT expiration errors
    if (error.response?.status === 401 && 
        (errorMessage.includes("jwt expired") || errorMessage.includes("Token expired"))) {
      errorMessage = "Your session has expired. Please log in again.";
    }

    if (setErrorMessage) {
      setErrorMessage(errorMessage);
    }

    // Create error object with original error for further handling
    const enhancedError = new Error(errorMessage);
    enhancedError.response = error.response;
    enhancedError.originalError = error;
    
    throw enhancedError; // Ensure correct error propagation
  }
};


// ✅ Ensure every request method handles decryption
const apiService = {
  get: (endpoint, setErrorMessage) => request("get", endpoint, null, "json", setErrorMessage),
  post: (endpoint, data, setErrorMessage) => request("post", endpoint, data, "json", setErrorMessage),
  put: (endpoint, data, setErrorMessage) => request("put", endpoint, data, "json", setErrorMessage),
  delete: (endpoint, setErrorMessage) => request("delete", endpoint, null, "json", setErrorMessage),
  patch: (endpoint, data, setErrorMessage) => request("patch", endpoint, data, "json", setErrorMessage),
};

export default apiService;
